import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  RefreshCw, 
  Database, 
  Users, 
  Car, 
  Clock, 
  TrendingUp,
  ChevronDown,
  ChevronRight,
  Building
} from 'lucide-react';

const SilverLayer = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [operationalStatus, setOperationalStatus] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryResponse, statusResponse] = await Promise.all([
        axios.get('/api/silver/summary'),
        axios.get('/api/silver/status')
      ]);
      setSummary(summaryResponse.data);
      setOperationalStatus(statusResponse.data.operational_status || []);
    } catch (error) {
      console.error('Error fetching silver layer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  // Group operational status by source and data_type
  const groupedOperationalStatus = () => {
    const groups = {};
    operationalStatus.forEach(item => {
      const key = `${item.source}:${item.data_type}`;
      if (!groups[key]) {
        groups[key] = {
          source: item.source,
          data_type: item.data_type,
          items: []
        };
      }
      groups[key].items.push(item);
    });

    // Sort groups by source, then data_type
    return Object.entries(groups).sort(([keyA], [keyB]) => {
      const [sourceA, dataTypeA] = keyA.split(':');
      const [sourceB, dataTypeB] = keyB.split(':');
      if (sourceA !== sourceB) {
        return sourceA.localeCompare(sourceB);
      }
      return dataTypeA.localeCompare(dataTypeB);
    });
  };

  // Get summary stats for a group
  const getGroupSummary = (items) => {
    const totalRecords = items.reduce((sum, item) => sum + (item.record_count || 0), 0);
    const latestUpdate = items.reduce((latest, item) => {
      const itemDate = new Date(item.last_updated_at || 0);
      return itemDate > latest ? itemDate : latest;
    }, new Date(0));
    const tenantCount = items.length;
    const hasMultipleTenants = items.some(item => item.tenant_id);

    return { 
      totalRecords, 
      latestUpdate: latestUpdate.getTime() > 0 ? latestUpdate : null, 
      tenantCount,
      hasMultipleTenants
    };
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : 'Never';
  };

  const getSourceIcon = (source) => {
    switch (source.toLowerCase()) {
      case 'servicetitan':
        return <Users className="h-5 w-5 text-blue-400" />;
      case 'adp':
        return <Clock className="h-5 w-5 text-green-400" />;
      case 'azuga':
        return <Car className="h-5 w-5 text-orange-400" />;
      default:
        return <Database className="h-5 w-5 text-gray-400" />;
    }
  };

  const getSourceColor = (source) => {
    switch (source.toLowerCase()) {
      case 'servicetitan':
        return 'text-blue-600';
      case 'adp':
        return 'text-green-600';
      case 'azuga':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2">Loading silver layer data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Silver Layer</h1>
          <p className="text-gray-600">Operational Data Tables</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* ServiceTitan Summary */}
          <div className="bg-white rounded-lg shadow border-l-4 border-blue-500 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">ServiceTitan</h3>
                <p className="text-sm text-gray-600">CRM Data</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Employees:</span>
                <span className="font-medium">{summary.summary.servicetitan.employees}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Technicians:</span>
                <span className="font-medium">{summary.summary.servicetitan.technicians}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Shifts:</span>
                <span className="font-medium">{summary.summary.servicetitan.shifts}</span>
              </div>
            </div>
          </div>

          {/* ADP Summary */}
          <div className="bg-white rounded-lg shadow border-l-4 border-green-500 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">ADP</h3>
                <p className="text-sm text-gray-600">Payroll Data</p>
              </div>
              <Clock className="h-8 w-8 text-green-600" />
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Workers:</span>
                <span className="font-medium">{summary.summary.adp.workers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Time Cards:</span>
                <span className="font-medium">{summary.summary.adp.timecards}</span>
              </div>
            </div>
          </div>

          {/* Azuga Summary */}
          <div className="bg-white rounded-lg shadow border-l-4 border-orange-500 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Azuga</h3>
                <p className="text-sm text-gray-600">Fleet Data</p>
              </div>
              <Car className="h-8 w-8 text-orange-600" />
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Drivers:</span>
                <span className="font-medium">{summary.summary.azuga.drivers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Vehicles:</span>
                <span className="font-medium">{summary.summary.azuga.vehicles}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Assignments:</span>
                <span className="font-medium">{summary.summary.azuga.assignments}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Operational Table Status */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Operational Table Status</h3>
          <p className="text-sm text-gray-600">Current status and record counts for all operational tables grouped by source and data type</p>
        </div>
        <div className="space-y-1">
          {groupedOperationalStatus().map(([groupKey, group]) => {
            const isExpanded = expandedGroups.has(groupKey);
            const summary = getGroupSummary(group.items);
            
            return (
              <div key={groupKey} className="border-b border-gray-100 last:border-b-0">
                {/* Group Header */}
                <div 
                  className="px-6 py-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => toggleGroup(groupKey)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {isExpanded ? (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      )}
                      {getSourceIcon(group.source)}
                      <div>
                        <h4 className={`font-medium ${getSourceColor(group.source)}`}>
                          {group.source.toUpperCase()} - {group.data_type.replace('_', ' ')}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {summary.hasMultipleTenants ? (
                            <>
                              {summary.tenantCount} tenant{summary.tenantCount !== 1 ? 's' : ''} • 
                              <span className="ml-1">Multi-tenant data</span>
                            </>
                          ) : (
                            <>
                              {summary.tenantCount} table{summary.tenantCount !== 1 ? 's' : ''} • 
                              <span className="ml-1">Single source data</span>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {summary.totalRecords.toLocaleString()} records
                      </p>
                      <p className="text-xs text-gray-500">
                        Last: {summary.latestUpdate ? formatDate(summary.latestUpdate) : 'Never'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {group.source.toLowerCase() === 'servicetitan' && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Tenant ID
                            </th>
                          )}
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Table Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Record Count
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Updated
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {group.items.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            {group.source.toLowerCase() === 'servicetitan' && (
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Building className="h-4 w-4 text-gray-400 mr-2" />
                                  <span className="text-sm font-medium text-blue-600">
                                    {item.tenant_id || 'Unknown'}
                                  </span>
                                </div>
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.table_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(item.record_count || 0).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(item.last_updated_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(item.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SilverLayer; 